/**
 * @file Components/Ui/AnimatedButton.jsx
 * @description Reusable animated button with ripple effect, loading state,
 * success confirmation, and smooth hover/tap animations.
 * Used for Search, Book Now, Login, Signup, and all CTA buttons.
 */

import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Check } from 'lucide-react';

const AnimatedButton = ({
  children,
  onClick,
  type = 'button',
  loading = false,
  success = false,
  disabled = false,
  variant = 'primary',    // 'primary' | 'secondary' | 'danger' | 'outline' | 'gradient'
  size = 'md',            // 'sm' | 'md' | 'lg' | 'xl'
  fullWidth = false,
  icon: Icon = null,
  iconPosition = 'left',
  className = '',
  ...props
}) => {
  const [ripples, setRipples] = useState([]);
  const buttonRef = useRef(null);

  const addRipple = (e) => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const size = Math.max(rect.width, rect.height) * 2;
    const id = Date.now();
    setRipples(prev => [...prev, { x, y, size, id }]);
    setTimeout(() => setRipples(prev => prev.filter(r => r.id !== id)), 600);
  };

  const handleClick = (e) => {
    if (disabled || loading) return;
    addRipple(e);
    onClick?.(e);
  };

  const variantStyles = {
    primary: 'bg-[#243185] hover:bg-[#1a2463] text-white shadow-lg shadow-[#243185]/20 hover:shadow-xl hover:shadow-[#243185]/30',
    secondary: 'bg-gray-800 hover:bg-gray-900 text-white shadow-lg shadow-gray-800/20',
    danger: 'bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/20',
    outline: 'bg-transparent border-2 border-[#243185] text-[#243185] hover:bg-[#243185] hover:text-white',
    gradient: 'bg-gradient-to-r from-[#243185] to-[#4f5fd6] hover:from-[#1a2463] hover:to-[#3d4bc4] text-white shadow-lg shadow-[#243185]/25 hover:shadow-xl hover:shadow-[#243185]/35',
  };

  const sizeStyles = {
    sm: 'px-4 py-2 text-xs font-semibold rounded-lg gap-1.5',
    md: 'px-6 py-2.5 text-sm font-bold rounded-lg gap-2',
    lg: 'px-8 py-3 text-base font-bold rounded-xl gap-2',
    xl: 'px-12 py-3.5 text-base font-bold rounded-full gap-2 tracking-wide uppercase',
  };

  const successStyles = success
    ? 'bg-emerald-500 hover:bg-emerald-500 shadow-emerald-500/30 text-white'
    : '';

  return (
    <motion.button
      ref={buttonRef}
      type={type}
      onClick={handleClick}
      disabled={disabled || loading}
      whileHover={!disabled && !loading ? { scale: 1.03, y: -1 } : {}}
      whileTap={!disabled && !loading ? { scale: 0.97 } : {}}
      transition={{ type: 'spring', stiffness: 400, damping: 17 }}
      className={`
        relative overflow-hidden inline-flex items-center justify-center
        transition-all duration-300 ease-out
        ${variantStyles[variant] || variantStyles.primary}
        ${sizeStyles[size] || sizeStyles.md}
        ${successStyles}
        ${fullWidth ? 'w-full' : ''}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      {...props}
    >
      {/* Ripple effects */}
      {ripples.map(ripple => (
        <span
          key={ripple.id}
          className="absolute rounded-full bg-white/25 animate-[ripple_0.6s_ease-out]"
          style={{
            left: ripple.x - ripple.size / 2,
            top: ripple.y - ripple.size / 2,
            width: ripple.size,
            height: ripple.size,
          }}
        />
      ))}

      {/* Content */}
      <span className="relative z-10 flex items-center justify-center gap-inherit">
        {loading ? (
          <motion.span
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center gap-2"
          >
            <Loader2 size={size === 'sm' ? 14 : 18} className="animate-spin" />
            <span>Processing...</span>
          </motion.span>
        ) : success ? (
          <motion.span
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center gap-2"
          >
            <Check size={size === 'sm' ? 14 : 18} />
            <span>Done!</span>
          </motion.span>
        ) : (
          <>
            {Icon && iconPosition === 'left' && <Icon size={size === 'sm' ? 14 : size === 'xl' ? 20 : 16} />}
            {children}
            {Icon && iconPosition === 'right' && <Icon size={size === 'sm' ? 14 : size === 'xl' ? 20 : 16} />}
          </>
        )}
      </span>

      {/* Shine effect on hover */}
      {!disabled && !loading && (
        <motion.span
          className="absolute inset-0 z-0"
          initial={{ x: '-100%', opacity: 0 }}
          whileHover={{ x: '100%', opacity: 0.1 }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          style={{ background: 'linear-gradient(90deg, transparent, white, transparent)' }}
        />
      )}
    </motion.button>
  );
};

export default AnimatedButton;
